import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Blocking gate: if the logged-in merchant hasn't accepted the latest version of
// the Platform Terms or Privacy Policy, force review + acceptance before continuing.
export default function LegalReacceptGate() {
  const [docs, setDocs] = useState([]);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/legal/my-acceptances`);
        const outdated = (data.needs_reaccept || []).map(t => data.documents[t]).filter(Boolean);
        setDocs(outdated);
      } catch { /* not logged in or endpoint unavailable — do nothing */ }
    })();
  }, []);

  if (!docs.length) return null;

  const allChecked = docs.every(d => checked[d.type]);

  const acceptAll = async () => {
    if (!allChecked) return;
    setBusy(true);
    try {
      for (const d of docs) {
        await axios.post(`${API}/api/legal/accept`, { terms_type: d.type });
      }
      toast.success('Thank you — updated terms accepted.');
      setDocs([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record acceptance. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4" data-testid="legal-reaccept-gate">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">We've updated our terms</h2>
            <p className="text-sm text-slate-500">Please review and accept to continue using StayMii eTicket.</p>
          </div>
        </div>

        <div className="space-y-3 my-5">
          {docs.map(d => (
            <label key={d.type} className="flex items-start gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:border-cyan-300" data-testid={`reaccept-item-${d.type}`}>
              <input type="checkbox" checked={!!checked[d.type]} onChange={e => setChecked(c => ({ ...c, [d.type]: e.target.checked }))}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer shrink-0"
                data-testid={`reaccept-check-${d.type}`} />
              <span className="text-sm text-slate-700 leading-relaxed">
                I agree to the updated{' '}
                <a href={d.url} target="_blank" rel="noreferrer" className="text-cyan-700 font-semibold underline inline-flex items-center gap-0.5">
                  {d.title} <ExternalLink className="w-3 h-3" />
                </a>{' '}
                <span className="text-slate-400">(v{d.current_version})</span>
              </span>
            </label>
          ))}
        </div>

        <button onClick={acceptAll} disabled={!allChecked || busy}
          className="w-full h-12 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="reaccept-submit">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Accept & Continue'}
        </button>
      </div>
    </div>
  );
}
